import { Link } from "react-router-dom";
import { ROUTES } from "../../../../routes";
import { sectionPath } from "../../pageAddress";
import type {
  ImportedPage,
  ImportedPages,
  PageLinksLookup,
} from "../../pagesTransfer/pagesTransferTypes";
import styles from "./PagesImportModal.module.css";

type ImportResultListProps = {
  title: string;
  pages: ImportedPage[];
  pageLinks: PageLinksLookup;
};

type ImportedPageNameProps = {
  page: ImportedPage;
  pageLinks: PageLinksLookup;
};

function ImportedPageName({ page, pageLinks }: ImportedPageNameProps) {
  if (pageLinks.status === "failed") {
    return page.title;
  }
  const pageId = pageLinks.pageIdsByPath.get(sectionPath(page.section, page.slug));
  if (!pageId) {
    return page.title;
  }
  return <Link to={ROUTES.cms.pageDetail(pageId)}>{page.title}</Link>;
}

function ImportResultList({ title, pages, pageLinks }: ImportResultListProps) {
  if (pages.length === 0) {
    return null;
  }

  return (
    <section className={styles.resultGroup}>
      <h3 className={styles.resultTitle}>
        {title} ({pages.length})
      </h3>
      <ul className={styles.resultList}>
        {pages.map(page => (
          <li key={sectionPath(page.section, page.slug)}>
            <ImportedPageName page={page} pageLinks={pageLinks} />
            <span className={styles.slug}> {sectionPath(page.section, page.slug)}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

type ImportResultProps = {
  importedPages: ImportedPages;
};

export function ImportResult({ importedPages }: ImportResultProps) {
  return (
    <div className={styles.result} role="status">
      <p className={styles.resultHeading}>Import complete.</p>

      <ImportResultList
        title="Created"
        pages={importedPages.created}
        pageLinks={importedPages.pageLinks}
      />
      <ImportResultList
        title="Overwritten"
        pages={importedPages.overwritten}
        pageLinks={importedPages.pageLinks}
      />
      <ImportResultList
        title="Kept as they were"
        pages={importedPages.skipped}
        pageLinks={importedPages.pageLinks}
      />

      {importedPages.pageLinks.status === "failed" && (
        <p className={styles.hint}>
          Links to the pages couldn't be loaded. Find them in the Pages list.
        </p>
      )}

      <p className={styles.reminder}>
        Publish each page when you've checked it. Changes show on the site after the next sync.
      </p>
    </div>
  );
}
